import { installConsoleCapture } from './utils/consoleCapture';
installConsoleCapture(); // Must run before any other code logs

import { installGlobalErrorHandlers } from './services/errorReportService';
installGlobalErrorHandlers();

import { installPreloadErrorReload } from './utils/preloadErrorReload';
installPreloadErrorReload(); // Auto-reload stale lazy chunks after a deploy

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import i18n from './i18n';
import { detectInitialLanguageByCountry } from './services/countryDetector';
import { ToastProvider } from './contexts/ToastContext';

// One-shot IP-country detection on first launch. Runs in background so it
// doesn't block first paint; switches language reactively if a better fit
// than navigator.language is found.
detectInitialLanguageByCountry(i18n.language).then(lang => {
  if (lang && lang !== i18n.language) i18n.changeLanguage(lang);
}).catch(() => {});
import { LocationGateProvider } from './contexts/LocationGateContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import { ensurePersistentStorage } from './services/tileCache';

// Be om vedvarende lagring så flis-cachen ikke evictes av OS-et (kritisk på
// iOS). Kjører på ALLE plattformer, ikke bare web. Best effort — svelger feil.
ensurePersistentStorage().catch(() => {});

// Registrer Service Worker for PWA-støtte med update detection
// Skip på native plattformer — SW er ikke nødvendig der (push via Capacitor)
const isNativePlatform = typeof (window as any).Capacitor?.isNativePlatform === 'function'
  ? (window as any).Capacitor.isNativePlatform()
  : !!navigator.userAgent.match(/CapacitorHttp/i);
if ('serviceWorker' in navigator && !isNativePlatform) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(registration => {
      // Sjekk for oppdateringer hver time. Svelg feil — periodiske
      // update()-kall kan feile ved nettverksglipp/dev-restart, og
      // ufangede rejections lager støy i error-reporten.
      setInterval(() => {
        registration.update().catch(() => { /* ignore transient SW update errors */ });
      }, 1000 * 60 * 60);

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'activated' && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent('sw-updated'));
            }
          });
        }
      });

      // When a new SW version activates, reload the page so users always
      // run the latest code rather than a stale cached JS bundle.
      window.addEventListener('sw-updated', () => {
        window.location.reload();
      }, { once: true });
    }).catch(err => {
      console.log('ServiceWorker registration failed: ', err);
    });
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

// Isolated UI-kit preview (design-review Phase 1). Reachable only at /ui-kit
// or ?uikit — keeps the primitives' visual QA page out of the product bundle's
// main flow without touching App.tsx.
const isUiKit =
  window.location.pathname.replace(/\/+$/, '') === '/ui-kit' ||
  new URLSearchParams(window.location.search).has('uikit');
const UiKitDemo = isUiKit ? React.lazy(() => import('./components/ui/UiKitDemo')) : null;

// Fase 0-spike for reise-replay (eng-review-gate). Kun ?replaySpike — isolert
// fra App på samme måte som ui-kit, så spike-koden aldri rører produktflyten.
const isReplaySpike = new URLSearchParams(window.location.search).has('replaySpike');
const ReplaySpike = isReplaySpike ? React.lazy(() => import('./components/dev/ReplaySpike')) : null;

// Dev-demo av det ekte ReplayOverlay-komponentet med syntetiske besøk (?replayDemo).
const isReplayDemo = new URLSearchParams(window.location.search).has('replayDemo');
const ReplayOverlayDemo = isReplayDemo ? React.lazy(() => import('./components/dev/ReplayOverlayDemo')) : null;

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        {ReplayOverlayDemo ? (
          <React.Suspense fallback={null}>
            <ReplayOverlayDemo />
          </React.Suspense>
        ) : ReplaySpike ? (
          <React.Suspense fallback={null}>
            <ReplaySpike />
          </React.Suspense>
        ) : UiKitDemo ? (
          <React.Suspense fallback={null}>
            <UiKitDemo />
          </React.Suspense>
        ) : (
          <LocationGateProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </LocationGateProvider>
        )}
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
